import { Landmark, Ship, UtensilsCrossed, Waves, IdCard, MoonStar } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal, Overline } from "@/components/motion";
import { IMAGES } from "@/data/site";

const DAY1 = [
  { time: "08:00", title: "Plecare din Limanu", desc: "Cafeaua pe punte, pânzele sus. Lăsăm Marina Life Harbour în urmă și luăm drumul sudului." },
  { time: "13:00", title: "Cap Kaliakra", desc: "Oprim în golful de sub stâncile roșii pentru baie și prânz la bord, cu delfinii dacă au chef de spectacol." },
  { time: "18:00", title: "Sosire în Balcik", desc: "Acostăm în micul port bulgăresc. Seara: cină la o tavernă de pe faleză, cu vedere spre golfulețe." },
];

const DAY2 = [
  { time: "09:00", title: "Palatul Reginei Maria", desc: "Vizită la reședința de vară a Reginei Maria și la Grădina Botanică — una dintre cele mai frumoase de la Marea Neagră." },
  { time: "14:00", title: "Golfulețe ascunse", desc: "Ultima baie într-un loc liniștit de-a lungul coastei bulgărești, departe de orice plajă aglomerată." },
  { time: "17:00", title: "Retur la apus", desc: "Navigăm spre nord cu soarele în pupa. Sosire în Limanu pe întuneric, sub stele." },
];

const INFO = [
  { icon: IdCard, title: "Documente", text: "Carte de identitate sau pașaport valabil — traversăm granița pe apă, formalitățile le rezolvăm noi." },
  { icon: Ship, title: "Distanța", text: "Aproximativ 40 de mile marine de navigație reală, cu pânzele, nu cu motorul." },
  { icon: MoonStar, title: "Cazare", text: "O noapte la bord în portul Balcik sau la o pensiune din orașul vechi, la alegere." },
  { icon: UtensilsCrossed, title: "Bucătăria locală", text: "Midii, pește proaspăt și vinuri bulgărești la tavernele de pe faleză." },
  { icon: Landmark, title: "Istorie", text: "Palatul Reginei Maria, Grădina Botanică și casele albe agățate de stânci." },
  { icon: Waves, title: "Baie în larg", text: "Opriri de înot în ape limpezi, la adăpostul stâncilor de la Kaliakra." },
];

const Balcik = () => (
  <div data-testid="balcik-page">
    <PageHero
      testid="balcik-hero"
      image={IMAGES.balchik}
      alt="Stâncile spectaculoase de la Cap Kaliakra, lângă Balcik"
      overline="Excursie de 2 Zile · Bulgaria"
      titleLines={["Balcik,", "grădina de pe stânci."]}
      subtitle="Cea mai iubită rută a noastră: două zile de navigație până în orașul alb al Bulgariei, cu oprire sub stâncile de la Cap Kaliakra și cină pe faleză."
    />

    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <div className="grid grid-cols-1 gap-20 lg:grid-cols-2">
        <div>
          <Reveal>
            <Overline className="mb-6">Ziua 1 — Drumul spre Sud</Overline>
          </Reveal>
          {DAY1.map((step, i) => (
            <Reveal key={step.time} delay={i * 0.1}>
              <div className="flex gap-8 border-t border-white/10 py-8">
                <p className="w-16 shrink-0 font-serif text-2xl text-gold">{step.time}</p>
                <div>
                  <h3 className="font-serif text-xl text-ivory">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div>
          <Reveal>
            <Overline className="mb-6">Ziua 2 — Orașul Alb & Retur</Overline>
          </Reveal>
          {DAY2.map((step, i) => (
            <Reveal key={step.time} delay={i * 0.1}>
              <div className="flex gap-8 border-t border-white/10 py-8">
                <p className="w-16 shrink-0 font-serif text-2xl text-gold">{step.time}</p>
                <div>
                  <h3 className="font-serif text-xl text-ivory">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-y border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16">
          <Overline className="mb-6">Bun de Știut</Overline>
          <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl">
            Totul pregătit, <span className="italic text-gold">tu doar urci la bord.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {INFO.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06} className="bg-deep">
              <div className="h-full p-8">
                <item.icon className="h-5 w-5 text-gold" />
                <h3 className="mt-5 font-serif text-lg text-ivory">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-12 text-center font-serif text-2xl font-light text-ivory">
            De la <span className="text-gold">100 € / persoană</span>
            <span className="text-mist"> · minimum 4 persoane · include skipper, combustibil și cazare la bord</span>
          </p>
        </Reveal>
      </div>
    </section>

    <CtaBanner
      title="Balcik așteaptă. Pânzele sunt gata."
      subtitle="Excursiile la Balcik au loc de 2–3 ori pe lună în sezon. Rezervă-ți locul pentru vara lui 2027."
    />
  </div>
);

export default Balcik;
